import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Icon from '../Icon/Icon';
import { useNavigate } from 'react-router-dom';
import './SearchInput.scss';

interface SearchFormData {
  query: string;
}

const searchSchema = yup.object().shape({
  query: yup
    .string()
    .required('Digite algo para buscar')
    .min(2, 'Digite pelo menos 2 caracteres')
});

interface SearchInputProps {
  onSearch?: (query: string) => void;
  placeholder?: string;
}

interface MenuFeature {
  id: string;
  label: string;
  path?: string;
}

const MENU_FEATURES: MenuFeature[] = [
  // Perfil
  { id: 'sobre', label: 'Sobre mim', path: '/perfil/sobre' },
  { id: 'conquistas', label: 'Conquistas', path: '/perfil/conquistas' },
  { id: 'carteira', label: 'Carteira', path: '/perfil/carteira' },
  { id: 'interesses', label: 'Interesses', path: '/perfil/interesses' },
  { id: 'atividades', label: 'Atividades', path: '/perfil/atividades' },
  { id: 'seguranca', label: 'Segurança', path: '/perfil/seguranca' },
  { id: 'preferencias', label: 'Preferências', path: '/perfil/preferencias' },

  // Menu de Conta - Principal
  {
    id: 'atualizar-dados',
    label: 'Atualizar dados pessoais',
    path: '/perfil/sobre'
  },
  {
    id: 'sessoes-ativas',
    label: 'Gerenciar sessões ativas',
    path: '/perfil/seguranca'
  },
  {
    id: 'preferencias-comunicacao',
    label: 'Preferências de comunicação',
    path: '/perfil/preferencias'
  },
  {
    id: 'baixar-dados',
    label: 'Baixar dados',
    path: '/perfil/sobre?secao=baixar-dados'
  },
  {
    id: 'desativar-conta',
    label: 'Desativar conta',
    path: '/perfil/seguranca?secao=excluir-conta'
  },
  {
    id: 'excluir-conta',
    label: 'Excluir conta',
    path: '/perfil/seguranca?secao=excluir-conta'
  },
  {
    id: 'criar-senha',
    label: 'Criar senha',
    path: '/menu-de-conta/criar-senha'
  },

  // Atualizar Dados - Sub-páginas
  {
    id: 'dados-conta',
    label: 'Dados da conta',
    path: '/perfil/sobre'
  },
  {
    id: 'dados-pessoais',
    label: 'Dados pessoais',
    path: '/perfil/sobre?secao=detalhes-sobre-mim'
  },
  {
    id: 'endereco',
    label: 'Endereço',
    path: '/perfil/sobre?secao=lugares-onde-morei'
  },
  {
    id: 'cadastrar-telefone',
    label: 'Cadastrar telefone',
    path: '/perfil/sobre?secao=informacoes-de-contato'
  },
  {
    id: 'alterar-senha',
    label: 'Alterar senha',
    path: '/perfil/seguranca'
  },
  {
    id: 'alterar-email',
    label: 'Alterar e-mail',
    path: '/perfil/sobre?secao=informacoes-de-contato'
  },

  // Preferências de Comunicação - Sub-páginas
  {
    id: 'tipos-comunicados',
    label: 'Tipos de comunicados',
    path: '/perfil/preferencias'
  },
  {
    id: 'assuntos-comunicados',
    label: 'Assuntos dos comunicados',
    path: '/perfil/preferencias'
  },
  {
    id: 'notificacoes',
    label: 'Notificações',
    path: '/perfil/preferencias'
  },

  // Outras funcionalidades
  { id: 'termos-uso', label: 'Termos de uso', path: '/termos-de-uso' },
  {
    id: 'politica-privacidade',
    label: 'Política de privacidade',
    path: '/politica-de-privacidade'
  }
];

export default function SearchInput({
  onSearch,
  placeholder = 'Procure algo na sua Conta SBT'
}: SearchInputProps) {
  const navigate = useNavigate();
  const [showResults, setShowResults] = useState(false);
  const [filteredResults, setFilteredResults] = useState(MENU_FEATURES);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<SearchFormData>({
    resolver: yupResolver(searchSchema),
    mode: 'onSubmit'
  });

  const handleSearchSubmit = (data: SearchFormData) => {
    if (onSearch) {
      onSearch(data.query);
    }
  };

  const handleInputChange = (value: string) => {
    if (value.length >= 2) {
      const filtered = MENU_FEATURES.filter((feature) =>
        feature.label.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredResults(filtered);
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  };

  const handleResultClick = (feature: MenuFeature) => {
    if (feature.path) {
      navigate(feature.path);
    }

    setShowResults(false);
  };

  return (
    <div className="search-input-wrapper">
      <form
        className="search-input"
        onSubmit={handleSubmit(handleSearchSubmit)}
      >
        <Icon name="search" className="search-input__icon" />
        <input
          type="text"
          className="search-input__field"
          placeholder={placeholder}
          {...register('query', {
            onChange: (e) => handleInputChange(e.target.value)
          })}
          aria-label="Campo de busca"
        />
        {errors.query && (
          <span className="search-input__error">{errors.query.message}</span>
        )}
      </form>
      {showResults && filteredResults.length > 0 && (
        <div className="search-input__results">
          {filteredResults.map((feature) => (
            <button
              key={feature.id}
              className="search-input__result-item"
              onClick={() => handleResultClick(feature)}
              type="button"
            >
              <Icon name="arrowRight" className="search-input__result-icon" />
              <span>{feature.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
